import { getHostname } from "@/scripts/getHostname";
import { getToken, onMessage } from "firebase/messaging";
import { useEffect, useState } from "react";
import { ErrorTags, reportAppError } from "../errorReporter";
import { messaging } from "../firebaseConfig";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { addNotification } from "../store/slices/storeSlice";
import { endpoints } from "./endpoints";
import { getToken as getAuthToken, playNotificationSound } from "./utils";

const useNotifications = () => {
  const user = useAppSelector((state) => state.store.user);
  const [permission, setPermission] = useState(false);
  const dispatch = useAppDispatch();

  const submitFcmInfo = async ({ token, topic }) => {
    try {
      const res = await (
        await fetch(endpoints.submitFcmInfo, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: getAuthToken(),
          },
          body: JSON.stringify({
            token,
            topic,
          }),
        })
      ).json();

      if (res.status === 200) {
        localStorage.setItem("fcm_token", token);
        localStorage.setItem(`subscribed_to_fcm_${topic}`, "1");
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      console.error(err);
      reportAppError({
        e: err,
        info: err.message,
        errorTag: ErrorTags.SubmitFcmInfo,
      });
    }
  };

  function requestPermission() {
    console.log("Requesting permission...");
    Notification?.requestPermission().then((permission) => {
      if (permission === "granted") {
        console.log("Notification permission granted.");
        setPermission(true);
      } else {
        console.log("Unable to get permission to notify.");
      }
    });
  }

  const attemptGetToken = async () => {
    try {
      const currentToken = await getToken(messaging, {
        vapidKey:
          "BCimMc2GrI7MZ77gLNfr7IMUiXXUrqYVxCNrLifT7nsiiBJpYN1eoiAnZLe0jKEmar3lCm7jC8phNN3CrHBiszs",
      });

      if (currentToken) {
        const topic = getHostname();
        console.info("token: " + currentToken);

        if (localStorage.getItem(`subscribed_to_fcm_${topic}`) !== "1") {
          submitFcmInfo({
            token: currentToken,
            topic,
          });
        }

        if (localStorage.getItem(`subscribed_to_fcm_global`) !== "1") {
          submitFcmInfo({
            token: currentToken,
            topic: "global",
          });
        }
      } else {
        console.log(
          "No registration token available. Request permission to generate one."
        );
      }
    } catch (err) {
      // Check if the error is due to blocked permissions
      if (err?.code === "messaging/permission-blocked") {
        console.log("Notification permissions are blocked by the user.");
        // Don't report to Sentry - this is a user choice, not an app error
        return;
      }

      // Only report unexpected errors to Sentry
      console.error("An error occurred while retrieving token. ", err);
      reportAppError({
        e: err,
        info: `Failed to get FCM token: ${err.message}`,
        errorTag: ErrorTags.SubmitFcmInfo,
      });
    }
  };

  useEffect(() => {
    onMessage(messaging, (payload) => {
      console.log('cewda', payload.data.portal, getHostname())
      if (user && payload.data.portal === getHostname()) {
        console.info("Message received. ", payload);
        dispatch(
          addNotification({
            ID: new Date().getTime(),
            title: payload.notification?.title,
            body: payload.notification?.body,
          })
        );
        playNotificationSound();
      } else {
        new Notification(payload.notification?.title, {
          body: payload.notification?.body,
        });
      }
    });

    // Check notification permission state before attempting to get token
    if (!("Notification" in window)) {
      console.log("This browser does not support notifications.");
      return;
    }

    const currentPermission = Notification.permission;

    if (currentPermission === "granted") {
      // Permission already granted, attempt to get token
      attemptGetToken();
    } else if (currentPermission === "default") {
      // Permission not yet requested, ask the user
      requestPermission();
    } else if (currentPermission === "denied") {
      // Permission explicitly denied/blocked - don't attempt to get token
      console.log("Notification permissions are denied. Notifications will not work.");
      // Don't report this to Sentry - it's a user choice
    }
  }, [permission, user]);

  return null;
};

export default useNotifications;
